import { SelfServiceError } from './Errors/SelfServiceError';
import {
    Order,
    OrderChangeRequestItem,
    OrderDetails,
    RefundQueryResult,
    TicketHolder,
} from '@/Services/BKAPI/models';
import { fromAPITicketHolder } from '@/Services/BKAPI/Reducers/SelfServiceReducer';
import { fromAPIToOrderDetails } from '@/Services/BKAPI/Reducers/OrderDetailsReducer';
import { fromAPIToOrder } from '@/Services/BKAPI/Reducers/OrderReducer';
import { fromAPIAvailableRefund } from '@/Services/BKAPI/Reducers/RefundReducer';
import type { ILogger } from '@/Contracts';
import {
    SelfServiceApi,
    SelfServiceApiCreateSelfServiceTicketHolderRequest,
    SelfServiceApiUpdateSelfServiceTicketHolderRequest,
} from '@bookingkit/api-v4';
import type { LimitedAuthRequest } from '@bookingkit/api-v4';
import { TicketHolderDataType } from '@/components/TicketHolder/composables/types';
import { format } from 'date-fns';

export class SelfService {
    private readonly connection: SelfServiceApi;
    private authToken?: string;
    private readonly logger?: ILogger;
    constructor(
        connection: SelfServiceApi,
        authToken?: string,
        logger?: ILogger
    ) {
        this.connection = connection;
        this.authToken = authToken;
        this.logger = logger;
    }

    setAuthToken(authToken?: string): void {
        this.authToken = authToken;
    }

    private getHeaders(): { authorization?: string } {
        return this.authToken
            ? { authorization: `Bearer ${this.authToken}` }
            : {};
    }

    async patchTicketHolder(
        vendorId: string,
        orderId: string,
        ticketHolderId: string,
        ticketHolderPatchRequest: any
    ): Promise<TicketHolder> {
        const params = ticketHolderPatchRequest;
        if (ticketHolderPatchRequest.dateOfBirth instanceof Date) {
            const formattedDate = format(
                ticketHolderPatchRequest.dateOfBirth,
                'yyyy-MM-dd'
            );
            params.dateOfBirth = formattedDate;
        }

        const headers = this.getHeaders();
        const { data } = await this.connection.updateSelfServiceTicketHolder(
            {
                vendorId,
                orderId,
                ticketHolderId,
                updateSelfServiceTicketHolderRequest: params,
            } as SelfServiceApiUpdateSelfServiceTicketHolderRequest,
            { headers }
        );
        if (!data) {
            throw SelfServiceError.fromMissingProperty('data', 'object', data);
        }
        return fromAPITicketHolder(data);
    }

    async createTicketHolder(
        vendorId: string,
        orderId: string,
        ticketHolderData: TicketHolderDataType
    ): Promise<TicketHolder> {
        const headers = this.getHeaders();
        const params = ticketHolderData;
        if (ticketHolderData.dateOfBirth instanceof Date) {
            params.dateOfBirth = format(
                ticketHolderData.dateOfBirth,
                'yyyy-MM-dd'
            );
        }
        const { data } = await this.connection.createSelfServiceTicketHolder(
            {
                vendorId,
                orderId,
                createSelfServiceTicketHolderRequest: params,
            } as SelfServiceApiCreateSelfServiceTicketHolderRequest,
            { headers }
        );

        if (!data) {
            throw SelfServiceError.fromMissingProperty('data', 'object', data);
        }
        return fromAPITicketHolder(data);
    }

    async getOrderDetails(
        vendorId: string,
        orderId: string
    ): Promise<OrderDetails> {
        const headers = this.getHeaders();

        const { data } = await this.connection.getSelfServiceOrder(
            { vendorId, orderId },
            { headers }
        );
        if (!data) {
            throw SelfServiceError.fromMissingProperty('data', 'object', data);
        }
        return fromAPIToOrderDetails(data);
    }

    async listOrders(vendorId: string): Promise<Order[]> {
        const headers = this.getHeaders();
        const { data } = await this.connection.listSelfServiceOrders(
            { vendorId },
            { headers }
        );
        this.logger?.logDebug('listSelfServiceOrders raw response', data);
        if (!data?.data) {
            throw SelfServiceError.fromMissingProperty('data', 'object', data);
        }
        return data.data.map(fromAPIToOrder);
    }

    async getAvailableRefund(
        vendorId: string,
        orderId: string,
        orderItemIds: string[]
    ): Promise<RefundQueryResult> {
        const headers = this.getHeaders();
        const { data } =
            await this.connection.getSelfServiceOrderAvailableRefund(
                { vendorId, orderId, orderItemIds },
                { headers }
            );
        this.logger?.logDebug(
            'getSelfServiceOrderAvailableRefund raw response',
            data
        );
        if (!data) {
            throw SelfServiceError.fromMissingProperty('data', 'object', data);
        }
        return fromAPIAvailableRefund(data);
    }

    async getTicketHolderList(
        vendorId: string,
        orderId: string
    ): Promise<TicketHolder[]> {
        const headers = this.getHeaders();
        const { data } = await this.connection.listSelfServiceTicketHolders(
            { vendorId, orderId },
            { headers }
        );
        if (!data?.data) {
            throw SelfServiceError.fromMissingProperty('data', 'object', data);
        }
        return data.data.map(fromAPITicketHolder);
    }

    async getTicketHolderPhoto(args: {
        vendorId: string;
        orderId: string;
        ticketHolderId: string;
    }): Promise<string> {
        const headers = this.getHeaders();
        const response = await this.connection.getSelfServiceTicketHolderPhoto(
            args,
            { headers }
        );
        return response?.data?.url;
    }
    async createOrderChange(
        vendorId: string,
        orderId: string,
        changes: OrderChangeRequestItem[]
    ): Promise<string> {
        const headers = this.getHeaders();
        const { data } = await this.connection.selfServiceOrderChangeCreate(
            {
                vendorId,
                orderId,
                selfServiceOrderChangesRequest: { changes },
            },
            { headers }
        );
        if (!data?.id) {
            throw SelfServiceError.fromMissingProperty(
                'id',
                'string',
                data?.id
            );
        }
        return data.id;
    }

    async issueLimitedAuth(
        vendorId: string,
        code: string,
        email: string
    ): Promise<string> {
        const limitedAuthRequest: LimitedAuthRequest = { code, email };
        const { data } = await this.connection.issueLimitedAuth({
            vendorId,
            limitedAuthRequest,
        });
        if (!data?.token) {
            throw SelfServiceError.fromMissingProperty(
                'token',
                'string',
                data?.token
            );
        }
        return data.token;
    }

    async postPhoto(args: {
        vendorId: string;
        orderId: string;
        ticketHolderId: string;
        photo: File;
        tosAcceptance: boolean;
    }): Promise<void> {
        const headers = this.getHeaders();

        const { vendorId, orderId, ticketHolderId, photo, tosAcceptance } =
            args;
        await this.connection.uploadSelfServicePhoto(
            {
                vendorId,
                orderId,
                ticketHolderId,
                photo,
                tosAcceptance: tosAcceptance
                    ? (1 as unknown as true)
                    : (0 as unknown as false),
            },
            {
                headers,
            }
        );
    }
}

export default SelfService;
